import type { DayCardFilm, MapConfig } from '../api/types'
import type { AnimationValues } from '../features/settings/AnimationFields'

/**
 * The switches of the day film (#139, 1.118.0): follow, tilt, profile and
 * the map, since 1.129.0 the relief (#140). **Only in the sheet "Welcher Tag?"** (decided: "Nur im
 * Blatt") -- no column in the trip and none in the administration.
 *
 * Since 1.125.0 they are the one film's switches, all off at the start: that
 * is the film as it always was (from use: "Codebasis ‚einfach' und ‚komplex'
 * zusammenlegen"). What a card was made with is stored with it on the server
 * (`DayCardFilm`) and stands in the sheet again for "Neu erstellen" -- from
 * use: "wenn die Streckenstatistik geändert wird, sollen die ursprünglichen
 * Werte angezeigt werden". The device no longer remembers a choice of its
 * own (`tb-tour-options` until 1.124.0): a second memory beside the card's
 * would answer the same question from two places.
 */
export interface TourOptions {
  /** A: the camera follows the vehicle. */
  follow: boolean
  /** B: the oblique view. */
  tilt: boolean
  /** C: the profile of the heights. */
  profile: boolean
  /** D: `trip` is the trip's map, otherwise a key of `map_choices`. */
  map: string
  /**
   * E: the ground with its relief from MapLibre (#140, 1.129.0). It brings
   * its own map, so D has no effect while it is on.
   */
  terrain: boolean
  /**
   * F: the vehicle as a 3D model (1.137.0). On by default, from use: a new
   * film gets it where the device can draw it (`canDraw3d()`); a card from
   * before has none and opens with it off.
   */
  vehicle3d: boolean
}

export const TOUR_DEFAULTS: TourOptions = { follow: false, tilt: false, profile: false, map: 'trip', terrain: false, vehicle3d: true }

/**
 * The choice as it goes from the sheet to the film: the options and the map
 * they name, resolved from `map_choices` -- `null` is the trip's map. The
 * licence text comes with it from the server (`MapSources`), never from here.
 */
export type TourChoice = TourOptions & { source: MapConfig | null }

/** What a card stores (1.125.0): the animation fields and the switches. */
export function filmOfCard(values: AnimationValues, tour: TourOptions): DayCardFilm {
  return {
    anim: values.daycard_anim,
    mode: values.daycard_anim_mode,
    seconds: values.daycard_anim_seconds,
    max: values.daycard_anim_max,
    rate: values.daycard_anim_rate,
    pause: values.daycard_anim_pause,
    pins: values.daycard_anim_pins,
    follow: tour.follow,
    tilt: tour.tilt,
    profile: tour.profile,
    map: tour.map,
    terrain: tour.terrain,
    vehicle3d: tour.vehicle3d,
  }
}

/** The sheet's fields from what a card stored. */
export function fieldsOfCard(film: DayCardFilm): { values: AnimationValues; tour: TourOptions } {
  return {
    values: {
      daycard_anim: film.anim,
      daycard_anim_mode: film.mode,
      daycard_anim_seconds: film.seconds,
      daycard_anim_max: film.max,
      daycard_anim_rate: film.rate,
      daycard_anim_pause: film.pause,
      daycard_anim_pins: film.pins,
    },
    // A card from before 1.129.0 has no `terrain`: it was made without
    // relief; one from before 1.137.0 no `vehicle3d`: its vehicle was the symbol.
    tour: { follow: film.follow, tilt: film.tilt, profile: film.profile, map: film.map, terrain: film.terrain === true, vehicle3d: film.vehicle3d === true },
  }
}
